import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listarTurnosPorCliente } from '../../services/turneroService';
import { estiloEstado } from '../../constants/estadosTurno';
import { etiquetaArea } from '../../constants/areasAtencion';
import { etiquetaResultadoAtencion, etiquetaMotivoNoAvance } from '../../constants/resultadosAtencion';
import { turnoANombreDeOtraPersona } from '../../utils/nombrePersona';
import { ModalDetalleTurno } from '../../pages/Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import '../../style/components/HistorialTurnosCliente.css';

/**
 * Historial de visitas de un cliente, como grilla de tarjetas.
 *
 * POR QUÉ EXISTE
 * Hasta ahora no había forma de responder "¿cuántas veces vino esta persona y qué se resolvió
 * cada vez?". El dato estaba en la base pero no había vínculo turno↔cliente (lo agregó V222) ni
 * pantalla que lo mostrara. Sin esto, el operador atiende a alguien que ya vino tres veces sin
 * enterarse de por qué las tres anteriores no derivaron en un trámite.
 *
 * LO MISMO EN DOS LUGARES
 * Se usa igual desde la ficha del cliente (VistaDetalladaCliente) y desde la ficha de atención
 * (VistaDetalladaAtencion). Es un solo componente y no dos copias porque son literalmente la
 * misma pregunta hecha desde dos pantallas distintas.
 *
 * SOBRE EL MODAL
 * ModalDetalleTurno sólo renderiza el contenido: la cáscara del overlay la pone el que lo usa
 * (mismo patrón que PanelDiario). Y recibe SIEMPRE onAsistioRegistrar, porque TurneroPage lo
 * invoca sin guarda de null al pasar a ASISTIO — omitirlo rompe la pantalla en ese click.
 */
export default function HistorialTurnosCliente({
    cuil,
    titulo = 'Historial de turnos',
    idTurnoActual = null,
    onTurnoActualizado,
    compacto = false,
    // En la ficha de atención el historial es "Visitas anteriores": el turno que se está
    // atendiendo ya está en pantalla, repetirlo en la lista solo confunde.
    ocultarTurnoActual = false,
}) {
    const navigate = useNavigate();
    const [turnos, setTurnos] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');
    const [turnoDetalle, setTurnoDetalle] = useState(null);
    // Solo aplica en modo compacto: ahí se muestran 4 filas y el resto queda detrás de "Mostrar
    // más" para no competir por espacio con el resto de la ficha de atención. Es estado de
    // presentación puro, no dispara ninguna carga nueva.
    const [mostrarTodas, setMostrarTodas] = useState(false);

    const cargar = useCallback(async () => {
        if (!cuil) {
            setTurnos([]);
            return;
        }
        setCargando(true);
        setError('');
        try {
            setTurnos(await listarTurnosPorCliente(cuil));
        } catch (e) {
            setError('No se pudo cargar el historial de turnos.');
        } finally {
            setCargando(false);
        }
    }, [cuil]);

    useEffect(() => { cargar(); }, [cargar]);

    // El modal devuelve el turno ya actualizado: se refresca la lista y se avisa al padre, que
    // puede tener sus propios datos derivados del turno (la ficha de atención, por ejemplo).
    const alActualizar = useCallback((actualizado) => {
        setTurnoDetalle(actualizado);
        cargar();
        if (onTurnoActualizado) onTurnoActualizado(actualizado);
    }, [cargar, onTurnoActualizado]);

    const alEliminar = useCallback(() => {
        setTurnoDetalle(null);
        cargar();
        if (onTurnoActualizado) onTurnoActualizado(null);
    }, [cargar, onTurnoActualizado]);

    if (!cuil) return null;

    const esTurnoActual = (turno) => idTurnoActual != null && String(idTurnoActual) === String(turno.idTurno);
    const turnosListados = ocultarTurnoActual ? turnos.filter((t) => !esTurnoActual(t)) : turnos;

    const hayTurnos = turnosListados.length > 0;
    // En modo compacto se listan 4 visitas y el resto queda colapsado; en el modo tarjeta
    // (VistaDetalladaCliente) siempre se muestran todas, como hoy.
    const turnosVisibles = compacto && !mostrarTodas ? turnosListados.slice(0, 4) : turnosListados;
    const restantes = turnosListados.length - turnosVisibles.length;

    return (
        <div className={`historial-turnos-section${compacto ? ' historial-turnos-section--compacto' : ''}`}>
            <div className="historial-turnos-header">
                <h2 className="historial-turnos-title">
                    <i className="bi bi-clock-history" />
                    {titulo}
                    {hayTurnos && (
                        <span className="historial-turnos-count">
                            {turnosListados.length} turno{turnosListados.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </h2>
            </div>

            <div className={compacto ? 'historial-turnos-list' : 'historial-turnos-grid'} role="list">
                {cargando && !hayTurnos && (
                    <div className="historial-turnos-empty">
                        <i className="bi bi-hourglass-split" />
                        <p>Cargando historial…</p>
                    </div>
                )}

                {!cargando && error && (
                    <div className="historial-turnos-error">
                        <i className="bi bi-exclamation-triangle" />
                        <p>{error}</p>
                    </div>
                )}

                {/* El caso vacío es el más común: la mayoría de la gente que saca turno todavía
                    no es cliente del estudio. Tiene que verse deliberado, no roto. */}
                {!cargando && !error && !hayTurnos && (
                    <div className={`historial-turnos-empty${compacto ? ' historial-turnos-empty--compacto' : ''}`}>
                        <i className={`bi ${ocultarTurnoActual ? 'bi-stars' : 'bi-calendar-x'}`} />
                        <p>
                            {ocultarTurnoActual
                                ? 'Es su primera visita: no tiene turnos anteriores.'
                                : 'Este cliente todavía no tiene turnos registrados.'}
                        </p>
                    </div>
                )}

                {turnosVisibles.map((turno) => (
                    <TarjetaTurno
                        key={turno.idTurno}
                        turno={turno}
                        esActual={esTurnoActual(turno)}
                        onAbrir={() => setTurnoDetalle(turno)}
                        compacto={compacto}
                    />
                ))}
            </div>

            {compacto && restantes > 0 && (
                <button type="button" className="historial-turnos-mostrar-mas" onClick={() => setMostrarTodas(true)}>
                    Mostrar {restantes} visita{restantes !== 1 ? 's' : ''} más
                </button>
            )}

            {turnoDetalle && (
                <div className={turneroStyles.overlay} onClick={() => setTurnoDetalle(null)}>
                    <div
                        className={`${turneroStyles.contenidoModal} ${turneroStyles.contenidoModalDetalle}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ModalDetalleTurno
                            turnoSel={turnoDetalle}
                            onCerrar={() => setTurnoDetalle(null)}
                            onActualizado={alActualizar}
                            onEliminado={alEliminar}
                            onAsistioRegistrar={(actualizado) =>
                                navigate(`/recepcion/atencion/${actualizado.idTurno}`)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

/** Una visita. Muestra cuándo fue, quién atendió y en qué terminó. */
function TarjetaTurno({ turno, esActual, onAbrir, compacto }) {
    const estilo = estiloEstado(turno.estado);
    const resultado = etiquetaResultadoAtencion(turno.resultadoAtencion);
    const motivoNoAvance = etiquetaMotivoNoAvance(turno.motivoNoAvance);

    const abrirConTeclado = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAbrir();
        }
    };

    // Modo compacto (ficha de atención): una fila angosta en vez de la tarjeta completa, para
    // competir menos por espacio con el resto del formulario. El modo tarjeta (por defecto, usado
    // también por VistaDetalladaCliente) no cambia una línea más abajo.
    if (compacto) {
        const hora = turno.horario ? String(turno.horario).substring(0, 5) : '';
        return (
            <div
                role="listitem"
                tabIndex={0}
                className={`historial-turno-row${esActual ? ' historial-turno-row--actual' : ''}`}
                title="Ver el detalle de esta atención"
                onClick={onAbrir}
                onKeyDown={abrirConTeclado}
            >
                <div className="historial-turno-row-icono" style={{ background: estilo.bg, color: estilo.text }}>
                    <i className={`bi ${estilo.icono}`} />
                </div>
                <div className="historial-turno-row-cuerpo">
                    <div className="historial-turno-row-cabecera">
                        <span className="historial-turno-row-fecha">
                            {formatearFecha(turno.fechaTurno)}
                            {hora && <span className="historial-turno-row-hora"> · {hora}</span>}
                        </span>
                        <span
                            className="historial-turno-card-estado"
                            style={{ background: estilo.bg, color: estilo.text, borderColor: estilo.border }}
                        >
                            {estilo.etiqueta}
                        </span>
                        {esActual && (
                            <span
                                className="historial-turno-card-estado"
                                style={{ background: '#ede9fe', color: '#5b21b6', borderColor: '#c4b5fd' }}
                            >
                                Este turno
                            </span>
                        )}
                    </div>
                    <div className="historial-turno-row-meta">
                        <span><i className="bi bi-person-badge" />{turno.operadorNombre || turno.usuarioAtiendeNombre || 'Sin registrar'}</span>
                        <span><i className="bi bi-signpost-split" />{etiquetaArea(turno.atencion)}</span>
                        {resultado && <span><i className="bi bi-flag" />{resultado}</span>}
                    </div>
                    {turno.motivo && (
                        <div className="historial-turno-row-motivo" title={turno.motivo}>
                            <i className="bi bi-chat-left-quote" />
                            <span>{turno.motivo}</span>
                        </div>
                    )}
                    {motivoNoAvance && (
                        <div className="historial-turno-row-noavance">
                            <strong>No avanzó:</strong> {motivoNoAvance}
                            {turno.observacionMotivoNoAvance && ` — ${turno.observacionMotivoNoAvance}`}
                        </div>
                    )}
                </div>
                <i className="bi bi-chevron-right historial-turno-row-flecha" />
            </div>
        );
    }

    return (
        <div
            role="listitem"
            tabIndex={0}
            className={`historial-turno-card${esActual ? ' historial-turno-card--actual' : ''}`}
            title="Ver el detalle de esta atención"
            onClick={onAbrir}
            onKeyDown={abrirConTeclado}
        >
            <div className="historial-turno-card-head">
                <div
                    className="historial-turno-card-icon"
                    style={{ background: estilo.bg, color: estilo.text }}
                >
                    <i className={`bi ${estilo.icono}`} />
                </div>
                <div className="historial-turno-card-body">
                    <h5 className="historial-turno-card-fecha">
                        {formatearFecha(turno.fechaTurno)}
                        {turno.horario ? ` · ${String(turno.horario).substring(0, 5)}` : ''}
                    </h5>
                    <span
                        className="historial-turno-card-estado"
                        style={{ background: estilo.bg, color: estilo.text, borderColor: estilo.border }}
                    >
                        {estilo.etiqueta}
                    </span>
                    {esActual && (
                        <span
                            className="historial-turno-card-estado"
                            style={{ background: '#ede9fe', color: '#5b21b6', borderColor: '#c4b5fd', marginLeft: '0.35rem' }}
                        >
                            Este turno
                        </span>
                    )}
                </div>
                <i className="bi bi-arrow-right-short historial-turno-card-arrow" />
            </div>

            <div className="historial-turno-card-foot">
                {turnoANombreDeOtraPersona(turno) && (
                    <div className="historial-turno-dato">
                        <i className="bi bi-person" />
                        <span><strong>A nombre de: </strong>{turnoANombreDeOtraPersona(turno)}</span>
                    </div>
                )}
                <div className="historial-turno-dato">
                    <i className="bi bi-person-badge" />
                    <span>
                        <strong>Atendió: </strong>
                        {turno.operadorNombre || turno.usuarioAtiendeNombre || 'Sin registrar'}
                    </span>
                </div>
                <div className="historial-turno-dato">
                    <i className="bi bi-signpost-split" />
                    <span><strong>Área: </strong>{etiquetaArea(turno.atencion)}</span>
                </div>
                {resultado && (
                    <div className="historial-turno-dato">
                        <i className="bi bi-flag" />
                        <span><strong>Resultado: </strong>{resultado}</span>
                    </div>
                )}
                {motivoNoAvance && (
                    <div className="historial-turno-noavance">
                        <strong>No avanzó:</strong> {motivoNoAvance}
                        {turno.observacionMotivoNoAvance && (
                            <span className="historial-turno-noavance-detalle">
                                {turno.observacionMotivoNoAvance}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** "2026-09-10" → "10/09/2026". Se arma a mano para no depender de la zona horaria. */
function formatearFecha(iso) {
    if (!iso) return 'Sin fecha';
    const partes = String(iso).substring(0, 10).split('-');
    if (partes.length !== 3) return String(iso);
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
}
